/**
 * Settlement Service for Thaklis Pro
 *
 * TC-side wrapper around the unified Invoice Payments backend.
 * A "settlement" is an `invoice_payments` row with `initiator = TC`.
 * Settlement screens (list, wizard, detail) call this service; under
 * the hood it hits `/api/invoice-payments` filtered to TC initiator,
 * and `/api/invoices/outstanding/{orgId}` for the wizard's invoice
 * picker.
 *
 * Backend response shapes:
 *  - GET /api/invoice-payments → list of `invoice_payments` rows
 *  - GET /api/invoice-payments/{id} → { payment, receipts }
 *  - POST /api/invoice-payments → ApiResponse wrapping
 *      { payment: {...}, receipts: [...] }
 *
 * Field naming on the wire is snake_case (Spring's global naming
 * strategy); this service maps the few used fields to camelCase
 * shapes the existing UI components expect.
 */

import ApiService from './api.service';
import WalletService from './wallet.service';

const unwrap = (response) => {
  const body = response?.data;
  // ApiResponse-wrapped: { success, data, count?, message? }
  if (body && typeof body === 'object' && 'data' in body && 'success' in body) {
    return body.data;
  }
  return body;
};

const mapPaymentRow = (p) => {
  if (!p) return null;
  return {
    id: p.id,
    settlementNumber: p.paymentNumber ?? p.payment_number,
    date: p.createdAt ?? p.created_at,
    customerId: p.internalOrganizationId ?? p.internal_organization_id,
    customerName: p.internalOrganizationName ?? p.internal_organization_name ?? p.organizationName ?? p.organization_name ?? '',
    mode: p.settlementMode ?? p.settlement_mode,
    initiator: p.initiator,
    paymentMethod: p.paymentMethod ?? p.payment_method,
    totalAmount: Number(p.totalAmount ?? p.total_amount ?? 0),
    invoiceCount: p.invoiceCount ?? p.invoice_count,
    status: p.status,
    notes: p.notes,
    walletBalanceBefore: null,
    walletBalanceAfter: null,
    cancelledAt: p.cancelledAt ?? p.cancelled_at,
    cancelReason: p.cancelReason ?? p.cancel_reason,
    createdAt: p.createdAt ?? p.created_at,
    createdBy: p.createdBy ?? p.created_by,
  };
};

const mapReceiptRow = (r) => {
  if (!r) return null;
  return {
    receiptId: r.id,
    receiptNumber: r.receiptNumber ?? r.receipt_number,
    invoiceId: r.invoiceId ?? r.invoice_id,
    invoiceNumber: r.invoiceNumber ?? r.invoice_number,
    invoiceDate: r.invoiceDate ?? r.invoice_date,
    amountSettled: Number(r.amount || 0),
    statusAfter: r.invoiceStatusAfter ?? r.invoice_status_after,
    status: r.status,
  };
};

const SettlementService = {
  /** List TC-side settlements (initiator=TC). */
  getAll: async (params = {}) => {
    const typingCenterId = params.typingCenterId;
    if (!typingCenterId) {
      return { data: [], total: 0 };
    }
    const res = await ApiService.getInvoicePayments({
      typingCenterId,
      initiator: 'TC',
    });
    const list = unwrap(res) || [];
    let mapped = list.map(mapPaymentRow);
    if (params.customerId) {
      mapped = mapped.filter((s) => s.customerId === params.customerId);
    }
    if (params.search) {
      const q = params.search.toLowerCase();
      mapped = mapped.filter(
        (s) =>
          (s.settlementNumber || '').toLowerCase().includes(q) ||
          (s.customerName || '').toLowerCase().includes(q),
      );
    }
    return { data: mapped, total: mapped.length };
  },

  /** Settlement detail with its receipts. */
  getById: async (id) => {
    const res = await ApiService.getInvoicePayment(id);
    const body = unwrap(res);
    if (!body) return null;
    const payment = mapPaymentRow(body.payment);
    if (!payment) return null;
    payment.invoices = (body.receipts || []).map(mapReceiptRow);
    return payment;
  },

  /** Statistics derived from the current TC's settlement list. */
  getStatistics: async (typingCenterId) => {
    const { data } = await SettlementService.getAll({ typingCenterId });
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthSettlements = data.filter(
      (s) => new Date(s.createdAt || s.date) >= monthStart,
    );
    const settlementsThisMonth = monthSettlements.length;
    const totalSettled = data.reduce((sum, s) => sum + (s.totalAmount || 0), 0);
    const averageSettlement = data.length ? totalSettled / data.length : 0;
    const invoicesSettled = data.reduce(
      (sum, s) => sum + (s.invoiceCount || 0),
      0,
    );
    return {
      settlementsThisMonth,
      totalSettled,
      averageSettlement,
      invoicesSettled,
    };
  },

  /** Outstanding invoices for a customer org — used by the wizard's
   *  Step 2 picker. Returns the FINAL invoices with
   *  customer_payment_status IN (UNPAID, PARTIAL). */
  getOutstandingInvoices: async (internalOrganizationId) => {
    if (!internalOrganizationId) return [];
    const res = await ApiService.getOutstandingInvoicesForOrg(
      internalOrganizationId,
    );
    const list = unwrap(res) || [];
    return list.map((inv) => ({
      id: inv.id,
      invoiceNumber: inv.invoiceNumber ?? inv.invoice_number,
      invoiceDate: inv.invoiceDate ?? inv.invoice_date,
      dueDate: inv.dueDate ?? inv.due_date,
      grandTotal: Number(inv.grandTotal ?? inv.grand_total ?? inv.totalAmount ?? inv.total_amount ?? 0),
      paidAmount: Number(inv.paidAmount ?? inv.paid_amount ?? 0),
      balanceAmount:
        Number(inv.amountDue ?? inv.amount_due ?? 0) ||
        Number(inv.grandTotal ?? inv.grand_total ?? inv.totalAmount ?? inv.total_amount ?? 0) -
          Number(inv.paidAmount ?? inv.paid_amount ?? 0),
      customerPaymentStatus: inv.customerPaymentStatus ?? inv.customer_payment_status,
    }));
  },

  /** Customer wallet balance (single `balance` column — no
   *  approved/unapproved split). */
  getCustomerWalletBalance: async (internalOrganizationId) => {
    if (!internalOrganizationId) return 0;
    const wallet = await WalletService.getCustomerWalletByOrg(
      internalOrganizationId,
    );
    if (!wallet) return 0;
    return Number(
      wallet.balance ?? wallet.approved_balance ?? wallet.approvedBalance ?? 0,
    );
  },

  /** Create a TC settlement.
   *
   *  @param {object} settlementData
   *  @param {string} settlementData.customerId  internal_organization_id
   *  @param {'AUTO'|'CUSTOM'} settlementData.mode
   *  @param {string[]} [settlementData.invoiceIds]  required for CUSTOM
   *  @param {string} [settlementData.notes]
   */
  create: async (settlementData) => {
    const payload = {
      internal_organization_id: settlementData.customerId,
      settlement_mode: settlementData.mode,
      notes: settlementData.notes || null,
    };
    if (settlementData.mode === 'CUSTOM') {
      payload.invoice_ids = settlementData.invoiceIds || [];
      if (settlementData.customAmounts && Object.keys(settlementData.customAmounts).length > 0) {
        payload.custom_amounts = settlementData.customAmounts;
      }
    }
    const res = await ApiService.createInvoicePaymentTC(payload);
    const body = unwrap(res);
    if (!body) return null;
    const payment = mapPaymentRow(body.payment);
    if (!payment) return null;
    payment.invoices = (body.receipts || []).map(mapReceiptRow);
    return payment;
  },

  formatCurrency: (amount) => {
    if (amount == null) return 'AED 0.00';
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 2,
    }).format(amount);
  },

  getPaymentMethodLabel: (method) =>
    ({
      CASH: 'Cash',
      BANK_TRANSFER: 'Bank Transfer',
      CHEQUE: 'Cheque',
      ONLINE: 'Online',
      WALLET: 'Wallet',
    }[method] || method),
};

export default SettlementService;
